import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ENROLL_FORM } from '../forms';

const navLinks = [
  { label: 'Home', to: '/#home' },
  { label: 'About', to: '/#about' },
  { label: 'Programs', to: '/#programs' },
  { label: 'Benefits', to: '/#benefits' },
  { label: 'Testimonials', to: '/#testimonials' },
  { label: 'Contact', to: '/#contact' },
];

function scrollToHash(hash, headerEl) {
  const el = document.getElementById(hash);
  if (!el) return false;

  const headerHeight = headerEl?.getBoundingClientRect().height ?? 96;
  const top = el.getBoundingClientRect().top + window.scrollY - headerHeight - 16;
  window.history.pushState(null, '', `/#${hash}`);
  window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  return true;
}

function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const handleNavClick = (event, to) => {
    const [path, hash] = to.split('#');
    if (!hash) {
      setMenuOpen(false);
      return;
    }
    if (path && path !== '/' && path !== window.location.pathname) {
      setMenuOpen(false);
      return;
    }

    const header = event.currentTarget.closest('header');
    if (scrollToHash(hash, header)) {
      event.preventDefault();
    }
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 bg-brand-white/95 backdrop-blur-md">
      <div className="page-shell flex items-center justify-between gap-3 py-3 sm:py-4">
        <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3">
          <img
            src="/images/bps-logo.png"
            alt="BPS Group"
            className="h-10 w-10 shrink-0 object-contain sm:h-12 sm:w-12 lg:h-14 lg:w-14"
          />
          <div className="min-w-0">
            <p className="truncate text-base font-bold leading-tight text-brand-navy sm:text-xl">
              BPS GROUP
            </p>
            <p className="hidden text-[10px] font-medium uppercase tracking-wide text-brand-muted sm:block sm:text-label-sm lg:text-label">
              Serving Excellence Since 2018
            </p>
          </div>
        </Link>

        <div className="hidden items-center gap-6 lg:flex xl:gap-10">
          <nav className="flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="text-nav text-brand-navy transition-colors hover:text-brand-primary"
                onClick={(event) => handleNavClick(event, link.to)}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <a
            href={ENROLL_FORM}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Enroll Now
          </a>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <a
            href={ENROLL_FORM}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary hidden px-3 py-2 text-label sm:inline-flex"
          >
            Enroll Now
          </a>
          <button
            type="button"
            className="flex h-10 w-10 items-center justify-center rounded-lg border border-brand-border text-brand-navy transition-colors hover:border-brand-primary hover:text-brand-primary"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? (
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
                <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {menuOpen ? (
        <div className="border-t border-brand-border/60 bg-brand-white lg:hidden">
          <nav className="page-shell flex flex-col gap-1 py-3">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.to}
                className="rounded-lg px-3 py-3 text-body-sm font-semibold text-brand-navy transition-colors hover:bg-brand-background hover:text-brand-primary"
                onClick={(event) => handleNavClick(event, link.to)}
              >
                {link.label}
              </Link>
            ))}
            <a
              href={ENROLL_FORM}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary mt-2 w-full sm:hidden"
            >
              Enroll Now
            </a>
          </nav>
        </div>
      ) : null}
    </header>
  );
}

export default Header;
